/**
 * ManageHabits
 * Parent settings component for managing child habits (add/edit/remove)
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Check, X } from 'lucide-react';
import { habitService } from '../services';
import type { HabitWithStatus } from '@kids-lms/types';

// Preset emoji options for quick selection
const EMOJI_PRESETS = ['🪥', '🤲', '👔', '📝', '📖', '⚽', '🥛', '🍎', '🧹', '🛏️', '🎵', '🧘', '💊', '🥗', '🚿'];

interface ManageHabitsProps {
  childId: string;
}

export default function ManageHabits({ childId }: ManageHabitsProps) {
  const [habits, setHabits] = useState<HabitWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newEmoji, setNewEmoji] = useState('🪥');
  const [editName, setEditName] = useState('');
  const [editEmoji, setEditEmoji] = useState('');

  useEffect(() => {
    loadHabits();
  }, [childId]);

  const loadHabits = async () => {
    try {
      setLoading(true);
      const data = await habitService.getHabits(childId);
      setHabits(data);
    } catch (err) {
      console.error('Failed to load habits:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      const habit = await habitService.addHabit(childId, newName.trim(), newEmoji);
      setHabits((prev) => [...prev, habit]);
      setNewName('');
      setNewEmoji('🪥');
      setShowAdd(false);
    } catch (err) {
      console.error('Failed to add habit:', err);
    }
  };

  const handleEdit = async (habitId: string) => {
    if (!editName.trim()) return;
    try {
      await habitService.updateHabit(habitId, { name: editName.trim(), emoji: editEmoji });
      setHabits((prev) =>
        prev.map((h) =>
          h.id === habitId ? { ...h, name: editName.trim(), emoji: editEmoji } : h
        )
      );
      setEditingId(null);
    } catch (err) {
      console.error('Failed to update habit:', err);
    }
  };

  const handleDelete = async (habitId: string) => {
    try {
      await habitService.deleteHabit(habitId);
      setHabits((prev) => prev.filter((h) => h.id !== habitId));
    } catch (err) {
      console.error('Failed to delete habit:', err);
    }
  };

  const startEdit = (habit: HabitWithStatus) => {
    setEditingId(habit.id);
    setEditName(habit.name);
    setEditEmoji(habit.emoji);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-kid-blue border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Habits list */}
      <div className="space-y-2">
        {habits.map((habit) => (
          <div
            key={habit.id}
            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-xl"
          >
            {editingId === habit.id ? (
              <div className="flex-1 flex items-center gap-2">
                <div className="flex gap-1 flex-wrap">
                  {EMOJI_PRESETS.slice(0, 8).map((e) => (
                    <button
                      key={e}
                      onClick={() => setEditEmoji(e)}
                      className={`text-lg p-1 rounded ${editEmoji === e ? 'bg-blue-100 ring-2 ring-blue-400' : 'hover:bg-gray-200'}`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleEdit(habit.id)}
                />
                <button onClick={() => handleEdit(habit.id)} className="text-green-600 hover:text-green-700">
                  <Check className="w-5 h-5" />
                </button>
                <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <span className="text-xl">{habit.emoji}</span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{habit.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => startEdit(habit)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(habit.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Add new habit */}
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 space-y-3">
              <div className="flex gap-1 flex-wrap">
                {EMOJI_PRESETS.map((e) => (
                  <button
                    key={e}
                    onClick={() => setNewEmoji(e)}
                    className={`text-lg p-1.5 rounded ${newEmoji === e ? 'bg-blue-100 ring-2 ring-blue-400' : 'hover:bg-blue-100'}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Habit name..."
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                />
                <button
                  onClick={handleAdd}
                  disabled={!newName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Add
                </button>
                <button
                  onClick={() => setShowAdd(false)}
                  className="px-3 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!showAdd && (
        <button
          onClick={() => setShowAdd(true)}
          className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 hover:text-blue-600 hover:border-blue-400 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Habit
        </button>
      )}
    </div>
  );
}
